// QR Kiosk v2 — KV2-2 service report DETAIL (read-only). v1 ServiceReportPage
// semantics × route-scoping. Number + status pill, title, meta grid (Opened /
// Urgency / Component-via-machineParts-join / Alarm + Closed / Total when solved),
// description, notes block (PG-10), files split by typeCode gated on FILTERED length
// (bug #12), solved block, status-gated actions. cost via Money.id (bug #2),
// openDate null -> em-dash via KDate (PG-11 / bug #6). Tokens only.
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import { getMachineParts, getServiceReport } from "../../publicSlice";
import {
  ServiceReportFile,
  ServiceReportFileTypeCode,
  ServiceReportStatusCode,
} from "../../models/serviceReportsModels";
import { KPage } from "../shared/KPage";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { formatClockTime } from "../shared/clock";
import { ReportDetailSkeleton } from "./ReportSkeletons";
import { KDate, UrgencyTag, ReportStatusPill, MetaCell } from "./ReportBits";
import { ReportFileChip } from "./ReportFileChip";
import { PhotoCarousel } from "./PhotoCarousel";
import ReportDetailActions from "./ReportDetailActions";
import { URGENCY_META, findPartTranslationKey, formatMoney, isImageFileName } from "./reportUtils";

const sectionBorderTop = "1px solid var(--border-soft)";

// §7.3 view-ticket wiring: image chips open a read-only PhotoCarousel (no delete). Each
// grid (report files vs solving files) is its own group → its own carousel scope.
function ChipGrid({ files }: { files: ServiceReportFile[] }) {
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const images = useMemo(
    () => files.map((f, i) => ({ f, i })).filter(({ f }) => !!f.content && isImageFileName(f.name)),
    [files]
  );
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 11 }}>
        {files.map((f, i) => {
          const imgPos = images.findIndex((x) => x.i === i);
          return (
            <ReportFileChip
              key={i}
              file={f}
              onOpenImage={imgPos >= 0 ? () => setViewerIdx(imgPos) : undefined}
            />
          );
        })}
      </div>
      {viewerIdx != null && (
        <PhotoCarousel
          images={images.map(({ f }) => ({ src: f.content!, name: f.name }))}
          initialIndex={viewerIdx}
          onClose={() => setViewerIdx(null)}
        />
      )}
    </>
  );
}

export function ReportDetailV2() {
  const { id } = useParams(); // :id = report NUMBER (not a GUID, not internalNumber)
  const dispatch = useAppDispatch();
  const { serviceReport, machineParts } = useAppSelector((s) => s.public);
  const { translate } = useTranslation(["public", "servicing"]);
  const { canWrite, canAdmin } = usePermission(Modules.Machinery); // SEC-REQ-3 / D4 — v1 parity

  useEffect(() => {
    if (!id) return;
    dispatch(getMachineParts()); // pending does NOT set publicSlice.loading (committed behavior)
    dispatch(getServiceReport(id));
  }, [id, dispatch]);

  // Route-scoped report — shared single field; stale detail must never flash.
  const r = serviceReport && id && serviceReport.number?.toLowerCase() === id.toLowerCase() ? serviceReport : undefined;
  const st = useSurfaceState("serviceReport", id);

  // Bug #12: each grid gates on ITS OWN filtered array (not the unfiltered files.length).
  const reportFiles = useMemo(
    () => r?.files?.filter((f) => f.typeCode === ServiceReportFileTypeCode.reportDocument) ?? [],
    [r?.files]
  );
  const solvedFiles = useMemo(
    () => r?.files?.filter((f) => f.typeCode === ServiceReportFileTypeCode.solvingReportDocument) ?? [],
    [r?.files]
  );

  const partKey = findPartTranslationKey(machineParts, r?.affectedPartCategoryId);
  const partName = partKey ? translate(`servicing:machineParts.parts.${partKey}`) : undefined;

  // §5 tree: no route-valid report → full error only when THIS key failed, else
  // skeleton (BUG-1). Route-valid report present → normal, or cached-dimmed on refetch fail.
  let body: React.ReactNode = null;
  if (!r) {
    body = st.failed ? (
      <ErrorCallout
        title={translate("kioskV2ReportLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => {
          if (id) {
            dispatch(getMachineParts());
            dispatch(getServiceReport(id));
          }
        }}
        retryLabel={translate("kioskV2Retry")}
      />
    ) : (
      <ReportDetailSkeleton />
    );
  } else {
    const solved = r.statusCode === ServiceReportStatusCode.solved;
    const urgencyMapped = r.urgency != null && !!URGENCY_META[r.urgency];
    const content = (
      <>
        <div
          className="kv2-enter"
          style={{
            margin: "14px 14px 0",
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            boxShadow: "var(--shadow)",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {/* Header — kiosk review §3: bumped for arm's-length reading */}
          <div style={{ padding: "14px 16px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 600, color: "var(--tier-accent)" }}>
                #{r.number}
              </span>
              <span style={{ flex: 1 }} />
              <ReportStatusPill solved={solved} />
            </div>
            <div
              style={{
                fontSize: 19,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "var(--ink)",
                marginTop: 7,
                lineHeight: 1.3,
              }}
            >
              {r.title}
            </div>
          </div>

          {/* Meta grid — cells ALWAYS render, em-dash for absent (KV2-2-c) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 14,
              padding: "2px 16px 6px",
              borderTop: sectionBorderTop,
            }}
          >
            <MetaCell label={translate("openDate")} value={<KDate date={r.openDate} />} />
            <MetaCell label={translate("urgency")} value={urgencyMapped ? <UrgencyTag urgency={r.urgency} /> : undefined} mono={false} />
            <MetaCell label={translate("componentAffected")} value={partName} mono={false} />
            <MetaCell label={translate("alarmCode")} value={r.alarmCode} />
            {solved && <MetaCell label={translate("closeDate")} value={<KDate date={r.closeDate} />} />}
            {solved && <MetaCell label={translate("solvedReportTotal")} value={formatMoney(r.cost)} />}
          </div>

          {/* Description (unconditional — v1 parity). Eyebrows use the recalibrated
              class color (--muted) — the old --faint overrides were the §3 complaint. */}
          <div style={{ padding: "10px 16px 14px", borderTop: sectionBorderTop }}>
            <div className="eyebrow" style={{ marginBottom: 5 }}>
              {translate("reportDescription")}
            </div>
            <div style={{ fontSize: 15, color: "var(--ink-2)", lineHeight: 1.6 }}>{r.description}</div>
            {reportFiles.length > 0 && <ChipGrid files={reportFiles} />}
          </div>

          {/* Technical notes (PG-10 — only when notes truthy) */}
          {r.notes && (
            <div style={{ padding: "10px 16px 14px", borderTop: sectionBorderTop }}>
              <div className="eyebrow" style={{ marginBottom: 5 }}>
                {translate("technicalNotes")}
              </div>
              <div style={{ fontSize: 15, color: "var(--ink-2)", lineHeight: 1.6 }}>{r.notes}</div>
            </div>
          )}

          {/* Solved block */}
          {solved && (
            <div
              style={{
                padding: "12px 16px 14px",
                borderTop: "1px solid var(--border)",
                background: "color-mix(in oklab, var(--green) 5%, var(--card))",
              }}
            >
              <div className="eyebrow" style={{ color: "color-mix(in oklab, var(--green) 70%, var(--ink))", marginBottom: 5 }}>
                {translate("kioskV2SolvingReport")}
              </div>
              <div style={{ fontSize: 15, color: "var(--ink-2)", lineHeight: 1.6 }}>{r.solvingDescription}</div>
              {solvedFiles.length > 0 && <ChipGrid files={solvedFiles} />}
            </div>
          )}
        </div>

        <ReportDetailActions report={r} canWrite={canWrite} canAdmin={canAdmin} />
      </>
    );
    // Cached report + refetch failed → dim the card AND the actions (Solve/Edit/PDF/
    // Delete on stale data is forbidden), under the committed callout + Retry.
    body = st.failed ? (
      <CachedErrorState
        title={translate("kioskV2ReportLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => {
          if (id) {
            dispatch(getMachineParts());
            dispatch(getServiceReport(id));
          }
        }}
        retryLabel={translate("kioskV2Retry")}
        cachedAt={st.loadedAt}
        cachedLabel={st.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(st.loadedAt) }) : undefined}
      >
        {content}
      </CachedErrorState>
    ) : (
      content
    );
  }

  return (
    <KPage
      backNested
      backTo={
        r
          ? `/public-v2/machines/${r.machineNumber}/service-reports/${r.statusCode === ServiceReportStatusCode.solved ? "archive" : "open"}`
          : undefined
      }
      machineNumber={r?.machineNumber}
    >
      {body}
    </KPage>
  );
}

export default ReportDetailV2;
